<script setup>
import { computed, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KanbanCardDetail from './KanbanCardDetail.vue';
import KanbanCardProductsAPI from 'dashboard/api/kanbanCardProducts';
import KanbanCardsAPI from 'dashboard/api/kanbanCards';
import KanbanCardProductsSummary from './KanbanCardProductsSummary.vue';
import KanbanCardProductsDropdown from './KanbanCardProductsDropdown.vue';
import CardLabels from 'dashboard/components/widgets/conversation/conversationCardComponents/CardLabels.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  columnId: {
    type: Number,
    required: true,
  },
  kanbanColumns: {
    type: Array,
    default: () => [],
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits([
  'delete-conversation',
  'update-conversation',
  'drag-start',
  'drag-end',
]);

const { t } = useI18n();
const store = useStore();

const showMenu = ref(false);
const showDetail = ref(false);
const showProductsDropdown = ref(false);
const showActionsMenu = ref(false);
const showProductsModal = ref(false);

// Product editing states
const editingProduct = ref(null);
const editQuantity = ref(1);
const productLoading = ref({});
const showDeleteModal = ref(false);
const productToDelete = ref(null);

// Modal de adicionar produto
const showAddProductModal = ref(false);
const selectedProductId = ref(null);
const addProductQuantity = ref(1);
const addProductLoading = ref(false);

// Computed
const kanbanCard = computed(() => {
  // Buscar o card específico do pipeline atual
  const card =
    props.conversation?.kanban_cards?.find(
      card => String(card.kanban_pipeline_id) === String(props.pipelineId)
    ) || null;

  return card;
});

// Usar o contact_name do kanban card (que já vem do backend)
const contactName = computed(() => {
  // Primeiro, tentar usar o contact_name do card (já calculado no backend)
  if (kanbanCard.value?.contact_name) {
    return kanbanCard.value.contact_name;
  }

  // Fallback: usar o contact da conversation (para casos onde o card não existe ainda)
  const contact = props.conversation?.contact;
  return (
    contact?.name ||
    contact?.email ||
    t('KANBAN.UNKNOWN_CONTACT') ||
    'Contato Desconhecido'
  );
});

const contactAvatar = computed(() => {
  return props.conversation?.contact?.avatar_url;
});

// Extrair nomes das labels para o componente CardLabels
const conversationLabels = computed(() => {
  const labels = props.conversation?.labels || [];
  // CardLabels espera um array de strings (nomes das labels)
  return labels.map(label => label.name || label.title || label);
});

const contactInitial = computed(() => {
  const name = contactName.value;
  return name ? name.charAt(0).toUpperCase() : '?';
});

const lastMessage = computed(() => {
  // Usar a última mensagem do objeto last_message (mesmo formato da aba de conversas)
  let message = props.conversation?.last_message?.content;

  // Se não tiver conteúdo, verificar se tem attachments
  if (!message && props.conversation?.last_message?.attachments?.length > 0) {
    return '📎 Anexo';
  }

  // Se ainda não tiver, mostrar mensagem padrão
  if (!message) return t('KANBAN.NO_MESSAGES') || 'Nenhuma mensagem';

  // Truncate message if too long
  const maxLength = 100;
  return message.length > maxLength
    ? message.substring(0, maxLength) + '...'
    : message;
});

const timeAgo = computed(() => {
  if (!props.conversation?.last_activity_at) return '';
  const date = new Date(props.conversation.last_activity_at);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));

  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d atrás`;
});

const assigneeName = computed(() => {
  return props.conversation.assignee?.name || t('KANBAN.UNASSIGNED');
});

const priorityClass = computed(() => {
  switch (props.conversation.priority) {
    case 'urgent':
      return 'border-l-red-500';
    case 'high':
      return 'border-l-orange-500';
    case 'medium':
      return 'border-l-yellow-500';
    case 'low':
      return 'border-l-green-500';
    default:
      return 'border-l-gray-300';
  }
});

const hasUnreadMessages = computed(() => {
  return props.conversation?.unread_count > 0;
});

// Computed para produtos
const productsCount = computed(() => {
  return kanbanCard.value?.kanban_card_products?.length || 0;
});

const totalValue = computed(() => {
  if (kanbanCard.value?.formatted_total_by_currency) {
    const totals = Object.values(kanbanCard.value.formatted_total_by_currency);
    return totals.length > 0 ? totals.join(', ') : 'R$ 0,00';
  }
  return 'R$ 0,00';
});

const products = computed(() => {
  return kanbanCard.value?.kanban_card_products || [];
});

// Produtos disponíveis para adicionar
const availableProducts = computed(() => {
  return store.getters['products/getProducts'] || [];
});

const selectedProduct = computed(() => {
  if (!selectedProductId.value) return null;
  return availableProducts.value.find(p => p.id === selectedProductId.value);
});

// Status do card
const cardStatus = computed(() => {
  return kanbanCard.value?.status || 'open';
});

const statusLabel = computed(() => {
  const statusMap = {
    open: 'Em Aberto',
    won: 'Ganho',
    lost: 'Perdido',
    no_response: 'Sem Resposta',
  };
  return statusMap[cardStatus.value] || 'Em Aberto';
});

const statusColor = computed(() => {
  const colorMap = {
    open: 'text-n-slate-11',
    won: 'text-n-green-11',
    lost: 'text-n-red-11',
    no_response: 'text-n-orange-11',
  };
  return colorMap[cardStatus.value] || 'text-n-slate-11';
});

// Inbox information
const inbox = computed(() => {
  return props.conversation?.inbox || {};
});

const inboxName = computed(() => {
  return inbox.value?.name || t('KANBAN.UNKNOWN_INBOX') || 'Canal Desconhecido';
});

const inboxIcon = computed(() => {
  const channelType = inbox.value?.channel_type;
  const medium = inbox.value?.medium;
  return getInboxIconByType(channelType, medium);
});

const handleDragStart = event => {
  event.dataTransfer.setData('conversation-id', props.conversation.id);
  event.dataTransfer.setData('from-column-id', props.columnId);
  event.currentTarget.classList.add('dragging');

  // Emit drag start event to parent
  emit('drag-start');
};

const handleDragEnd = event => {
  event.currentTarget.classList.remove('dragging');

  // Emit drag end event to parent
  emit('drag-end');
};

const navigateToConversation = () => {
  // NÃO navegar se o modal está aberto ou se o menu está aberto
  if (showDetail.value || showMenu.value) {
    return;
  }

  // Verificar se temos os dados necessários
  const conversationDisplayId =
    props.conversation?.display_id ?? props.conversation?.displayId;
  const conversationIdForUrl = conversationDisplayId ?? props.conversation?.id;

  if (!props.conversation?.inbox_id || !conversationIdForUrl) {
    return;
  }

  // Verificação adicional: se estamos no contexto do modal, não navegar
  // Isso pode acontecer se o evento ainda estiver propagando
  const isModalContext = document.querySelector('.modal-overlay');
  if (isModalContext) {
    return;
  }

  // Construir URL diretamente para a conversa
  const route = `/app/accounts/${props.conversation.account_id}/conversations/${conversationIdForUrl}`;

  // Abrir em nova aba
  window.open(route, '_blank');
};

const handleCloseDetail = async () => {
  try {
    // Usar nextTick para garantir que o fechamento seja assíncrono
    await nextTick();

    showDetail.value = false;
    showMenu.value = false; // Garantir que o menu também feche
  } catch (error) {
    // Forçar fechamento mesmo com erro
    showDetail.value = false;
    showMenu.value = false;
  }
};

const handleUpdateConversation = updatedConversation => {
  emit('update-conversation', updatedConversation);
};

const toggleMenu = event => {
  event.stopPropagation();
  event.preventDefault();
  showMenu.value = !showMenu.value;
};

const deleteConversation = event => {
  event.stopPropagation();
  event.preventDefault();
  showMenu.value = false;
  emit('delete-conversation', props.conversation.id);
};

// Métodos para produtos
const toggleProductsDropdown = () => {
  showProductsDropdown.value = !showProductsDropdown.value;
};

const openProductsModal = () => {
  showProductsModal.value = true;
};

const closeProductsModal = () => {
  showProductsModal.value = false;
};

const toggleActionsMenu = () => {
  showActionsMenu.value = !showActionsMenu.value;
};

const closeActionsMenu = () => {
  showActionsMenu.value = false;
};

const openCardDetails = event => {
  // IMPORTANTE: Parar propagação para evitar navigateToConversation
  event.stopPropagation();
  event.preventDefault();
  event.stopImmediatePropagation();

  showMenu.value = false; // Fechar menu se estiver aberto
  showDetail.value = true;
};

const openDeleteModal = () => {
  // Emitir evento para abrir modal de exclusão
  emit('delete-conversation', props.conversation.id);
};

// Funções de edição e exclusão de produtos
const editProduct = product => {
  editingProduct.value = product.id;
  editQuantity.value = product.quantity;
};

const cancelEditProduct = () => {
  editingProduct.value = null;
  editQuantity.value = 1;
};

const saveEditProduct = async productId => {
  if (!kanbanCard.value?.id) {
    useAlert('Erro: Card não encontrado');
    return;
  }

  try {
    productLoading.value[productId] = true;

    await KanbanCardProductsAPI.updateProduct(
      props.pipelineId,
      kanbanCard.value.id,
      productId,
      {
        quantity: editQuantity.value,
      }
    );

    // Atualizar o produto localmente na lista
    const productIndex = kanbanCard.value.kanban_card_products.findIndex(
      p => p.id === productId
    );
    if (productIndex !== -1) {
      kanbanCard.value.kanban_card_products[productIndex].quantity =
        editQuantity.value;
      // Recalcular o total_price
      kanbanCard.value.kanban_card_products[productIndex].total_price =
        kanbanCard.value.kanban_card_products[productIndex].quantity *
        kanbanCard.value.kanban_card_products[productIndex].unit_price;
    }

    // Emitir evento para atualizar o componente pai
    emit('update-conversation', props.conversation);

    // Emitir evento para notificar outras páginas sobre a mudança
    window.dispatchEvent(
      new CustomEvent('productsUpdated', {
        detail: {
          conversationId: props.conversation.id,
          action: 'kanban_product_updated',
          productId: productId,
          cardId: kanbanCard.value.id,
          source: 'kanban',
        },
      })
    );

    editingProduct.value = null;
    useAlert('Produto atualizado com sucesso');
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    useAlert('Erro ao atualizar produto');
  } finally {
    productLoading.value[productId] = false;
  }
};

const deleteProduct = product => {
  productToDelete.value = product;
  showDeleteModal.value = true;
};

const confirmDeleteProduct = async () => {
  if (!productToDelete.value) return;

  const productId = productToDelete.value.id;

  if (!kanbanCard.value?.id) {
    useAlert('Erro: Card não encontrado');
    return;
  }

  try {
    productLoading.value[productId] = true;

    await KanbanCardProductsAPI.removeProduct(
      props.pipelineId,
      kanbanCard.value.id,
      productId
    );

    // Remover o produto localmente da lista
    kanbanCard.value.kanban_card_products =
      kanbanCard.value.kanban_card_products.filter(p => p.id !== productId);

    // Emitir evento para atualizar o componente pai
    emit('update-conversation', props.conversation);

    // Emitir evento para notificar outras páginas sobre a mudança
    window.dispatchEvent(
      new CustomEvent('productsUpdated', {
        detail: {
          conversationId: props.conversation.id,
          action: 'kanban_product_removed',
          productId: productId,
          cardId: kanbanCard.value.id,
          source: 'kanban',
        },
      })
    );

    useAlert('Produto removido com sucesso');
  } catch (error) {
    console.error('Erro ao remover produto:', error);
    useAlert('Erro ao remover produto');
  } finally {
    productLoading.value[productId] = false;
    showDeleteModal.value = false;
    productToDelete.value = null;
  }
};

const cancelDeleteProduct = () => {
  showDeleteModal.value = false;
  productToDelete.value = null;
};

// Funções para adicionar produto
const openAddProductModal = () => {
  // Se o modal de produtos estiver aberto, fechar primeiro
  if (showProductsModal.value) {
    showProductsModal.value = false;
    // Pequeno delay para transição suave
    setTimeout(() => {
      showAddProductModal.value = true;
      selectedProductId.value = null;
      addProductQuantity.value = 1;
    }, 150);
  } else {
    showAddProductModal.value = true;
    selectedProductId.value = null;
    addProductQuantity.value = 1;
  }
};

const closeAddProductModal = () => {
  showAddProductModal.value = false;
  selectedProductId.value = null;
  addProductQuantity.value = 1;
};

const addProduct = async () => {
  if (!selectedProductId.value || addProductQuantity.value <= 0) return;

  if (!kanbanCard.value?.id) {
    useAlert('Erro: Card não encontrado');
    return;
  }

  try {
    addProductLoading.value = true;

    const productData = {
      product_id: selectedProductId.value,
      quantity: addProductQuantity.value,
    };

    await KanbanCardProductsAPI.addProduct(
      props.pipelineId,
      kanbanCard.value.id,
      productData
    );

    // Emitir evento para atualizar o componente pai
    emit('update-conversation', props.conversation);

    // Emitir evento para notificar outras páginas sobre a mudança
    // (mas não o próprio kanban, que já foi atualizado localmente)
    window.dispatchEvent(
      new CustomEvent('productsUpdated', {
        detail: {
          conversationId: props.conversation.id,
          action: 'kanban_product_added',
          productId: selectedProductId.value,
          cardId: kanbanCard.value.id,
          source: 'kanban', // Identificar que a mudança veio do kanban
        },
      })
    );

    closeAddProductModal();
    useAlert('Produto adicionado com sucesso');
  } catch (error) {
    console.error('Erro ao adicionar produto:', error);
    useAlert('Erro ao adicionar produto');
  } finally {
    addProductLoading.value = false;
  }
};

// Status update method
const updateCardStatus = async status => {
  if (!kanbanCard.value?.id) {
    console.error('Card ID not found');
    return;
  }

  try {
    await KanbanCardsAPI.updateCardStatus(
      props.pipelineId,
      kanbanCard.value.id,
      status
    );

    // Update the card locally
    kanbanCard.value.status = status;

    // Create updated conversation with the new card status
    const updatedConversation = {
      ...props.conversation,
      kanban_cards: props.conversation.kanban_cards?.map(card =>
        String(card.kanban_pipeline_id) === String(props.pipelineId)
          ? { ...card, status }
          : card
      ),
    };

    // Emit event to update parent component
    emit('update-conversation', updatedConversation);

    // Emit event to update Kanban board
    window.dispatchEvent(
      new CustomEvent('kanbanCardStatusUpdated', {
        detail: {
          conversationId: props.conversation.id,
          cardId: kanbanCard.value.id,
          status: status,
          pipelineId: props.pipelineId,
        },
      })
    );
  } catch (error) {
    console.error('Error updating card status:', error);
    useAlert('Erro ao atualizar status do card');
  }
};

// Função removida - status agora é gerenciado no nível do pipeline

// Função para obter o preço unitário correto
const getProductUnitPrice = product => {
  // Para kanban_card_products
  if (product.unit_price) {
    const price =
      typeof product.unit_price === 'number'
        ? product.unit_price
        : parseFloat(product.unit_price);
    return formatCurrency(price, product.currency);
  }

  // Para conversation_products
  if (product.product?.base_price) {
    const price =
      typeof product.product.base_price === 'number'
        ? product.product.base_price
        : parseFloat(product.product.base_price);
    return formatCurrency(price, product.product.currency);
  }

  // Fallback para formatted_price do produto
  if (product.product?.formatted_price) {
    return product.product.formatted_price;
  }

  return 'R$ 0,00';
};

// Função para formatar moeda
const formatCurrency = (value, currency = 'BRL') => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  switch (currency) {
    case 'USD':
      return `$ ${numValue.toFixed(2)}`;
    case 'EUR':
      return `€ ${numValue.toFixed(2)}`;
    case 'BRL':
    default:
      return `R$ ${numValue.toFixed(2)}`;
  }
};

// Listener para eventos de mudança de funil
const handleFunnelUpdate = event => {
  const { action, currentCardId } = event.detail;

  // Se um produto foi adicionado e é o mesmo card, forçar re-render
  if (action === 'create' && currentCardId === kanbanCard.value?.id) {
    // Forçar re-render do componente
    nextTick(() => {
      // O computed kanbanCard já deve detectar as mudanças automaticamente
    });
  }
};

// Fechar menu de ações ao clicar fora
const handleClickOutside = event => {
  if (showActionsMenu.value && !event.target.closest('.relative')) {
    showActionsMenu.value = false;
  }
};

onMounted(async () => {
  // Carregar produtos disponíveis
  try {
    await store.dispatch('products/get');
  } catch (error) {
    console.error('❌ Erro ao carregar produtos:', error);
  }

  window.addEventListener('funnelUpdated', handleFunnelUpdate);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('funnelUpdated', handleFunnelUpdate);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    class="kanban-card bg-n-background border border-n-weak rounded-lg p-3 transition-all duration-200 border-l-4 relative"
    :class="priorityClass"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Linha 1: Canal + Status + Tempo -->
    <div class="flex items-center justify-between gap-2 mb-2">
      <!-- Canal -->
      <div class="flex items-center gap-1 text-xs text-n-slate-11">
        <div
          class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-4"
        >
          <Icon
            :icon="inboxIcon"
            class="flex-shrink-0 text-n-slate-11 size-2.5"
          />
        </div>
        <span class="truncate">{{ inboxName }}</span>
      </div>

      <!-- Status -->
      <span
        class="text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
        :class="[
          {
            'bg-n-slate-3 text-n-slate-11': cardStatus === 'open',
            'bg-n-teal-3 text-n-teal-11': cardStatus === 'won',
            'bg-n-ruby-3 text-n-ruby-11': cardStatus === 'lost',
            'bg-n-amber-3 text-n-amber-11': cardStatus === 'no_response',
          },
        ]"
      >
        {{ statusLabel }}
      </span>

      <!-- Tempo -->
      <span class="text-xs text-n-slate-10 whitespace-nowrap">{{
        timeAgo
      }}</span>
    </div>

    <!-- Linha 2: Avatar + Nome + Prioridade + Ações -->
    <div class="flex items-center gap-2 mb-2">
      <!-- Avatar (menor) -->
      <div class="relative flex-shrink-0">
        <img
          v-if="contactAvatar"
          :src="contactAvatar"
          :alt="contactName"
          class="w-8 h-8 rounded-full object-cover"
        />
        <div
          v-else
          class="w-8 h-8 rounded-full bg-n-slate-9 flex items-center justify-center text-white text-xs font-medium"
        >
          {{ contactInitial }}
        </div>
      </div>

      <!-- Nome -->
      <div class="flex-1 min-w-0">
        <h4
          class="font-semibold text-n-slate-12 text-sm leading-tight truncate"
        >
          {{ contactName }}
        </h4>
      </div>

      <!-- Indicadores e Ações -->
      <div class="flex items-center gap-1 flex-shrink-0">
        <!-- Unread indicator -->
        <div v-if="hasUnreadMessages" class="relative">
          <div
            class="w-5 h-5 rounded-full flex items-center justify-center bg-yellow-500 shadow-sm"
          >
            <span class="text-white font-semibold text-xs">{{
              conversation.unread_count
            }}</span>
          </div>
        </div>

        <!-- Priority indicator -->
        <div
          v-if="conversation.priority"
          class="w-2 h-2 rounded-full"
          :class="{
            'bg-red-500': conversation.priority === 'urgent',
            'bg-orange-500': conversation.priority === 'high',
            'bg-yellow-500': conversation.priority === 'medium',
            'bg-green-500': conversation.priority === 'low',
          }"
        />

        <!-- Actions Menu -->
        <div class="relative">
          <button
            class="w-7 h-7 rounded-md flex items-center justify-center text-n-slate-11 border border-n-slate-6 hover:bg-n-slate-2 hover:border-n-slate-8 transition-all"
            title="Ações do card"
            @click.stop="toggleActionsMenu"
          >
            <i class="i-lucide-more-vertical size-4" />
          </button>

          <!-- Actions Dropdown Menu -->
          <div
            v-if="showActionsMenu"
            class="absolute right-0 top-8 z-50 w-48 bg-n-background border border-n-weak rounded-lg shadow-lg py-2"
            @click.stop
          >
            <!-- Status Actions -->
            <div v-if="kanbanCard">
              <div
                v-if="cardStatus === 'won' || cardStatus === 'lost'"
                class="px-3 py-2"
              >
                <button
                  class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 rounded-md transition-colors flex items-center gap-2"
                  @click.stop="
                    updateCardStatus('open');
                    closeActionsMenu();
                  "
                >
                  <i class="i-lucide-rotate-ccw size-4 text-blue-600" />
                  <span>Reabrir card</span>
                </button>
              </div>

              <div v-if="cardStatus === 'open'" class="px-3 py-2 space-y-1">
                <button
                  class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 rounded-md transition-colors flex items-center gap-2"
                  @click.stop="
                    updateCardStatus('won');
                    closeActionsMenu();
                  "
                >
                  <i class="i-lucide-check-circle size-4 text-green-600" />
                  <span>Marcar como Ganho</span>
                </button>
                <button
                  class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 rounded-md transition-colors flex items-center gap-2"
                  @click.stop="
                    updateCardStatus('lost');
                    closeActionsMenu();
                  "
                >
                  <i class="i-lucide-x-circle size-4 text-red-600" />
                  <span>Marcar como Perdido</span>
                </button>
              </div>

              <div class="border-t border-n-weak my-1" />
            </div>

            <!-- General Actions -->
            <div class="px-3 py-2 space-y-1">
              <button
                class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 rounded-md transition-colors flex items-center gap-2"
                @click.stop="
                  event => {
                    openCardDetails(event);
                    closeActionsMenu();
                  }
                "
              >
                <i class="i-lucide-info size-4 text-blue-600" />
                <span>Ver detalhes</span>
              </button>
              <button
                class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 rounded-md transition-colors flex items-center gap-2"
                @click.stop="
                  openDeleteModal();
                  closeActionsMenu();
                "
              >
                <i class="i-lucide-trash size-4 text-red-600" />
                <span>Excluir do funil</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Linha 3: Última Mensagem -->
    <div class="mb-2">
      <p class="text-xs text-n-slate-11 truncate">{{ lastMessage }}</p>
    </div>

    <!-- Tags da conversa -->
    <CardLabels
      v-if="conversationLabels.length"
      :conversation-labels="conversationLabels"
      class="mb-2"
    />

    <!-- Footer -->
    <div class="flex items-center justify-between text-xs text-n-slate-11">
      <div class="flex items-center gap-2">
        <i class="i-lucide-user" />
        <span>{{ assigneeName }}</span>
      </div>
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-1">
          <i class="i-lucide-message-circle" />
          <span>{{ conversation.messages_count || 0 }}</span>
        </div>
        <!-- Ícone de redirecionamento -->
        <button
          class="flex items-center gap-1 p-1 hover:bg-n-blue-11/10 rounded text-n-slate-11 hover:text-n-blue-11 transition-colors"
          title="Abrir conversa em nova aba"
          @click="navigateToConversation"
        >
          <i class="i-lucide-external-link size-3" />
        </button>
        <!-- Botão de produtos -->
        <div v-if="kanbanCard" class="relative group">
          <button
            v-if="kanbanCard.kanban_card_products?.length > 0"
            class="flex items-center gap-1 px-2 py-1 bg-n-blue-11/5 hover:bg-n-blue-11/10 border border-n-blue-11/20 rounded text-n-blue-11 transition-colors font-medium"
            @click.stop="openProductsModal"
          >
            <i class="i-lucide-shopping-cart size-3" />
            <span>{{ totalValue }}</span>
          </button>
          <button
            v-else
            class="flex items-center gap-1 px-2 py-1 bg-n-green-11/5 hover:bg-n-green-11/10 border border-n-green-11/20 rounded text-n-green-11 transition-colors"
            title="Adicionar Produto"
            @click.stop="showAddProductModal = true"
          >
            <i class="i-lucide-plus size-3" />
          </button>
          <!-- Tooltip -->
          <div
            v-if="kanbanCard.kanban_card_products?.length > 0"
            class="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
          >
            Ver produtos
            <!-- Seta do tooltip -->
            <div
              class="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Card Detail Modal -->
    <KanbanCardDetail
      :conversation="conversation"
      :kanban-card="kanbanCard"
      :kanban-columns="kanbanColumns"
      :pipeline-id="pipelineId"
      :show="showDetail"
      @close="handleCloseDetail"
      @update-conversation="handleUpdateConversation"
    />

    <!-- Add Product Modal -->
    <div
      v-if="showAddProductModal"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      @click.self="closeAddProductModal"
    >
      <div class="bg-n-background rounded-lg shadow-lg w-full max-w-md m-4">
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"
            >
              <i class="i-lucide-plus size-5 text-green-600" />
            </div>
            <div>
              <h3 class="text-base font-semibold text-n-slate-12">
                Adicionar Produto
              </h3>
              <p class="text-sm text-n-slate-11">
                Selecione um produto para adicionar ao card
              </p>
            </div>
          </div>

          <div class="space-y-4">
            <!-- Seleção de Produto -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Produto
              </label>
              <select
                v-model="selectedProductId"
                class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-11/20 bg-n-background text-n-slate-12"
                :disabled="availableProducts.length === 0"
              >
                <option value="">Selecione um produto</option>
                <option
                  v-for="product in availableProducts"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.name }} -
                  {{
                    product.formatted_price ||
                    `R$ ${product.base_price || product.value}`
                  }}
                </option>
              </select>
              <p
                v-if="availableProducts.length === 0"
                class="text-xs text-n-slate-11 mt-1"
              >
                Nenhum produto disponível. Carregando...
              </p>
            </div>

            <!-- Quantidade -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Quantidade
              </label>
              <input
                v-model.number="addProductQuantity"
                type="number"
                min="1"
                class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-11/20 bg-n-background text-n-slate-12"
              />
            </div>

            <!-- Preview do Produto -->
            <div
              v-if="selectedProduct"
              class="p-3 bg-n-slate-1 border border-n-weak rounded-lg"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                  :style="{ backgroundColor: selectedProduct.color }"
                >
                  <i class="i-lucide-package size-4" />
                </div>
                <div class="flex-1">
                  <h4 class="font-medium text-n-slate-12">
                    {{ selectedProduct.name }}
                  </h4>
                  <p class="text-sm text-n-slate-11">
                    {{
                      selectedProduct.formatted_price ||
                      `R$ ${selectedProduct.base_price || selectedProduct.value}`
                    }}
                    cada
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 mt-6">
            <button
              class="px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-solid-2 hover:bg-n-solid-3 rounded-lg transition-colors"
              @click="closeAddProductModal"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              :disabled="
                !selectedProductId ||
                addProductLoading ||
                availableProducts.length === 0
              "
              @click="addProduct"
            >
              <span v-if="addProductLoading">Adicionando...</span>
              <span v-else-if="availableProducts.length === 0">Nenhum produto disponível</span>
              <span v-else>Adicionar Produto</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Product Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-[65] flex items-center justify-center bg-black/50"
      @click.self="cancelDeleteProduct"
    >
      <div class="bg-n-background rounded-lg shadow-lg w-full max-w-md m-4">
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"
            >
              <i class="i-lucide-alert-triangle size-5 text-red-600" />
            </div>
            <div>
              <h3 class="text-base font-semibold text-n-slate-12">
                Confirmar Exclusão
              </h3>
              <p class="text-sm text-n-slate-11">
                Esta ação não pode ser desfeita
              </p>
            </div>
          </div>

          <div class="mb-6">
            <p class="text-n-slate-11">
              Tem certeza que deseja excluir o produto
              <strong>"{{ productToDelete?.product?.name || 'Produto' }}"</strong>
              desta conversa?
            </p>
          </div>

          <div class="flex items-center justify-end gap-3">
            <button
              class="px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-solid-2 hover:bg-n-solid-3 rounded-lg transition-colors"
              @click="cancelDeleteProduct"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              :disabled="productLoading[productToDelete?.id]"
              @click="confirmDeleteProduct"
            >
              <span v-if="productLoading[productToDelete?.id]">Excluindo...</span>
              <span v-else>Sim, Excluir</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Modal -->
    <div
      v-if="showProductsModal"
      class="fixed inset-0 z-[55] flex items-center justify-center bg-black/50"
      @click.self="closeProductsModal"
    >
      <div
        class="bg-n-background rounded-lg shadow-lg w-full max-w-2xl m-4 max-h-[80vh] flex flex-col"
      >
        <!-- Header -->
        <div class="p-6 border-b border-n-weak">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"
              >
                <i class="i-lucide-shopping-cart size-5 text-blue-600" />
              </div>
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  Produtos do Card
                </h3>
                <p class="text-sm text-n-slate-11">{{ contactName }}</p>
              </div>
            </div>
            <button
              class="w-8 h-8 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-slate-2 transition-colors"
              @click="closeProductsModal"
            >
              <i class="i-lucide-x size-5" />
            </button>
          </div>
        </div>

        <!-- Products List -->
        <div class="flex-1 overflow-y-auto p-6">
          <div class="space-y-3">
            <div
              v-for="product in products"
              :key="product.id"
              class="flex items-center gap-3 p-3 bg-n-solid-1 rounded-lg border border-n-weak hover:border-n-blue-11/30 transition-colors"
              @click.stop
            >
              <div
                class="w-4 h-4 rounded-full flex-shrink-0"
                :style="{
                  backgroundColor: product.product?.color || '#6366f1',
                }"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ product.product?.name || 'Produto' }}
                </p>
                <div
                  v-if="editingProduct === product.id"
                  class="flex items-center gap-2 mt-1"
                >
                  <input
                    v-model.number="editQuantity"
                    type="number"
                    min="1"
                    class="w-20 px-3 py-1 text-sm border border-n-weak rounded focus:outline-none focus:ring-2 focus:ring-n-blue-11/20"
                  />
                  <span class="text-sm text-n-slate-11">x {{ getProductUnitPrice(product) }}</span>
                  <div class="flex items-center gap-1">
                    <button
                      class="p-1 hover:bg-n-green-11/10 rounded text-n-slate-11 hover:text-n-green-11 transition-colors"
                      :disabled="productLoading[product.id]"
                      title="Salvar"
                      @click.stop="saveEditProduct(product.id)"
                    >
                      <i class="i-lucide-check size-4" />
                    </button>
                    <button
                      class="p-1 hover:bg-n-slate-11/10 rounded text-n-slate-11 hover:text-n-slate-12 transition-colors"
                      title="Cancelar"
                      @click.stop="cancelEditProduct"
                    >
                      <i class="i-lucide-x size-4" />
                    </button>
                  </div>
                </div>
                <p v-else class="text-sm text-n-slate-11">
                  {{ product.quantity }}x {{ getProductUnitPrice(product) }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-base font-semibold text-n-blue-11">
                  {{
                    product.formatted_total ||
                    `R$ ${(product.quantity * (product.product?.base_price || 0)).toFixed(2)}`
                  }}
                </span>
                <div
                  v-if="editingProduct !== product.id"
                  class="flex items-center gap-1"
                >
                  <button
                    class="p-2 hover:bg-n-blue-11/10 rounded text-n-slate-11 hover:text-n-blue-11 transition-colors"
                    title="Editar produto"
                    @click.stop="editProduct(product)"
                  >
                    <i class="i-lucide-edit size-4" />
                  </button>
                  <button
                    class="p-2 hover:bg-n-red-11/10 rounded text-n-slate-11 hover:text-n-red-11 transition-colors"
                    :disabled="productLoading[product.id]"
                    title="Excluir produto"
                    @click.stop="deleteProduct(product)"
                  >
                    <i class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-n-weak">
          <div class="flex items-center justify-between">
            <button
              class="flex items-center gap-2 px-4 py-2 bg-n-green-11/10 hover:bg-n-green-11/20 text-n-green-11 rounded-lg text-sm font-medium transition-colors"
              @click.stop="openAddProductModal"
            >
              <i class="i-lucide-plus size-4" />
              <span>Adicionar Produto</span>
            </button>
            <div class="text-right">
              <p class="text-sm text-n-slate-11 mb-1">Total</p>
              <p class="text-lg font-bold text-n-blue-11">{{ totalValue }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card {
  transition: all 0.2s ease;
}

/* Hover removido para evitar conflitos com modal */

.kanban-card.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
